import { useState, useRef, useCallback } from 'react';
import Modal from './Modal';
import Icon  from './Icon';
import { Storage } from '../storage';

// ─── CSV IMPORT MODAL ─────────────────────────────────────────────────────────
//
//  Props:
//    open       {boolean}
//    onClose    {function}
//    timelineId {string}
//    onImported {function(result)}  called with server response on success
//

const PREVIEW_ROWS = 5;

const CSVImportModal = ({ open, onClose, timelineId, onImported }) => {
  const [file,        setFile]        = useState(null);
  const [parsedRows,  setParsedRows]  = useState(null);  // parsed locally for preview
  const [dragOver,    setDragOver]    = useState(false);
  const [importing,   setImporting]   = useState(false);
  const [result,      setResult]      = useState(null);   // server response after import
  const [error,       setError]       = useState('');
  const fileInputRef  = useRef(null);

  const reset = () => {
    setFile(null);
    setParsedRows(null);
    setResult(null);
    setError('');
  };

  const handleClose = () => {
    if (importing) return;
    reset();
    onClose();
  };

  // ── Parse CSV client-side for preview ──────────────────────────────────────
  const parsePreview = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (!lines.length) return null;

    // Simple CSV split — handles quoted fields
    const splitLine = (line) => {
      const cells = [];
      let cur = '';
      let inQ  = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQ = !inQ; continue; }
        if (ch === ',' && !inQ) { cells.push(cur.trim()); cur = ''; continue; }
        cur += ch;
      }
      cells.push(cur.trim());
      return cells;
    };

    const headers = splitLine(lines[0]);
    const rows    = lines.slice(1, PREVIEW_ROWS + 1).map(l => splitLine(l));
    const total   = lines.length - 1; // subtract header
    return { headers, rows, total };
  };

  const handleFileAccepted = (f) => {
    setError('');
    setResult(null);
    if (!f.name.toLowerCase().endsWith('.csv')) {
      setError('Only .csv files are supported.');
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      setError('File exceeds the 2 MB size limit.');
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const preview = parsePreview(e.target.result || '');
      setParsedRows(preview);
    };
    reader.readAsText(f);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileAccepted(f);
  }, []);

  const handleDragOver  = (e) => { e.preventDefault(); setDragOver(true);  };
  const handleDragLeave = ()  => setDragOver(false);

  const handleFileInput = (e) => {
    const f = e.target.files[0];
    if (f) handleFileAccepted(f);
    e.target.value = '';
  };

  // ── Import ──────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!file || importing) return;
    setImporting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await Storage.importEventsFromCSV(timelineId, fd);
      setResult(res);
      onImported(res);
    } catch (err) {
      setError(err.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const handleTemplate = () => Storage.downloadCSVTemplate();

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Modal open={open} onClose={handleClose} title="Import Events from CSV" width={680}>

      {/* ── Template download hint ── */}
      <div className="imp-hint">
        <Icon name="info" size={14} />
        <span>
          Need a template?{' '}
          <button className="imp-link" onClick={handleTemplate}>Download CSV template</button>
        </span>
      </div>

      {/* ── Drop zone ── */}
      {!result && (
        <div
          className={`imp-dropzone${dragOver ? ' imp-dropzone--over' : ''}${file ? ' imp-dropzone--has-file' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !file && fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          aria-label="Drop CSV file here or click to browse"
          onKeyDown={(e) => e.key === 'Enter' && !file && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleFileInput}
          />
          {file ? (
            <div className="imp-file-selected">
              <Icon name="document" size={20} />
              <span className="imp-filename">{file.name}</span>
              <button
                className="imp-remove-file"
                onClick={(e) => { e.stopPropagation(); reset(); }}
                aria-label="Remove file"
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          ) : (
            <>
              <Icon name="upload" size={28} />
              <p className="imp-drop-label">Drop your CSV here or <span className="imp-link">browse</span></p>
              <p className="imp-drop-sub">Max 2 MB · .csv only</p>
            </>
          )}
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div className="imp-error">
          <Icon name="warning" size={14} />
          {error}
        </div>
      )}

      {/* ── Large-file warning ── */}
      {parsedRows && parsedRows.total > 200 && !result && (
        <div className="imp-warn">
          <Icon name="warning" size={14} />
          <span>
            <strong>{parsedRows.total.toLocaleString()} rows</strong> detected. Large imports may take a moment.
          </span>
        </div>
      )}

      {/* ── Preview table ── */}
      {parsedRows && !result && (
        <div className="imp-preview">
          <p className="imp-preview-label">
            Preview — first {Math.min(PREVIEW_ROWS, parsedRows.rows.length)} of {parsedRows.total.toLocaleString()} row{parsedRows.total !== 1 ? 's' : ''}
          </p>
          <div className="imp-table-wrap">
            <table className="imp-table">
              <thead>
                <tr>{parsedRows.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {parsedRows.rows.map((row, ri) => (
                  <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Result summary (shown after a successful import) ── */}
      {result && (
        <div className="imp-result">
          <div className="imp-result-stats">
            <div className="imp-stat imp-stat--ok">
              <span className="imp-stat-num">{result.imported}</span>
              <span className="imp-stat-lbl">imported</span>
            </div>
            {result.skipped > 0 && (
              <div className="imp-stat imp-stat--warn">
                <span className="imp-stat-num">{result.skipped}</span>
                <span className="imp-stat-lbl">skipped (duplicates)</span>
              </div>
            )}
            {result.errors.length > 0 && (
              <div className="imp-stat imp-stat--err">
                <span className="imp-stat-num">{result.errors.length}</span>
                <span className="imp-stat-lbl">errors</span>
              </div>
            )}
          </div>

          {result.errors.length > 0 && (
            <div className="imp-msg-list">
              <p className="imp-msg-list-title">Errors</p>
              {result.errors.slice(0, 10).map((e, i) => (
                <p key={i} className="imp-msg-item imp-msg-item--err">Row {e.row}: {e.message}</p>
              ))}
              {result.errors.length > 10 && (
                <p className="imp-msg-item imp-msg-item--err">…and {result.errors.length - 10} more</p>
              )}
            </div>
          )}

          {result.warnings.length > 0 && (
            <div className="imp-msg-list">
              <p className="imp-msg-list-title">Skipped (duplicates)</p>
              {result.warnings.slice(0, 5).map((w, i) => (
                <p key={i} className="imp-msg-item imp-msg-item--warn">{w.message}</p>
              ))}
              {result.warnings.length > 5 && (
                <p className="imp-msg-item imp-msg-item--warn">…and {result.warnings.length - 5} more</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Actions ── */}
      <div className="imp-actions">
        {result ? (
          <>
            <button className="btn-secondary" onClick={() => { reset(); }}>
              Import Another
            </button>
            <button className="btn-primary" onClick={handleClose}>
              Done
            </button>
          </>
        ) : (
          <>
            <button className="btn-secondary" onClick={handleClose} disabled={importing}>
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleImport}
              disabled={!file || importing}
            >
              {importing ? 'Importing…' : `Import${parsedRows ? ` ${parsedRows.total.toLocaleString()} row${parsedRows.total !== 1 ? 's' : ''}` : ''}`}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CSVImportModal;
